/**
 * 画像取得ユーティリティ
 * Admin管理画面用：GCSからサーバー経由で直接画像を取得（署名不要）
 */

/**
 * Cloudflare R2 の公開 CDN ベース URL（ビルド時に VITE_CLOUDFLARE_R2_CDN_PUBLIC_URL で注入）。
 * 未設定ならオリジン (/static/...) からそのまま配信。
 */
const R2_CDN_BASE: string = (import.meta.env.VITE_CLOUDFLARE_R2_CDN_PUBLIC_URL || "")
  .toString()
  .replace(/\/+$/, "");

/**
 * Admin用画像プロキシURLを生成
 * @param storagePath GCS上のストレージパス
 * @returns プロキシURL、無効な場合はnull
 *
 * storage_path のパターン別の解決ルール:
 *  - `http(s)://...` → そのまま（外部 URL）
 *  - `/static/...`   → VITE_CLOUDFLARE_R2_CDN_PUBLIC_URL が設定されていれば CDN へ書き換え
 *                       (未設定ならオリジン: nginx → /app/static)
 *  - `/api/...`      → そのまま（既に絶対パス）
 *  - その他          → `/api/admin/image/<path>` 経由（R2 + ローカルキャッシュ）
 */
export function getImageUrl(storagePath: string): string | null {
  if (!storagePath) {
    return null;
  }
  if (/^https?:\/\//i.test(storagePath)) {
    return storagePath;
  }
  if (storagePath.startsWith("/static/")) {
    return R2_CDN_BASE ? R2_CDN_BASE + storagePath : storagePath;
  }
  if (storagePath.startsWith("/api/")) {
    return storagePath;
  }
  return `/api/admin/image/${storagePath}`;
}

/**
 * 複数の画像URLを一括生成
 * @param storagePaths ストレージパスのリスト
 * @returns ストレージパス -> URLのマップ
 */
export function getMultipleImageUrls(
  storagePaths: string[]
): Map<string, string> {
  const result = new Map<string, string>();

  for (const storagePath of storagePaths) {
    if (!storagePath) continue;
    const url = getImageUrl(storagePath);
    if (url) {
      result.set(storagePath, url);
    }
  }

  return result;
}

// 後方互換性のためのエイリアス（非推奨）
/**
 * @deprecated getImageUrl を使用してください
 */
export async function getSignedUrl(objectName: string): Promise<string | null> {
  return getImageUrl(objectName);
}

/**
 * @deprecated getMultipleImageUrls を使用してください
 */
export async function getMultipleSignedUrls(
  objectNames: string[]
): Promise<Map<string, string>> {
  return getMultipleImageUrls(objectNames);
}

// キャッシュ関連（プロキシ使用時は不要だが、後方互換性のため残す）
export function clearCache(_objectName?: string): void {
  // プロキシ使用時はキャッシュ管理不要（ブラウザキャッシュに任せる）
}

export function cleanupExpiredCache(): number {
  return 0;
}

export function getCacheStats(): { total: number; valid: number } {
  return { total: 0, valid: 0 };
}
